'use client'

import React, { useRef, useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import Badge from '@/components/Shared/Badge'
import styles from './styles.module.scss'

interface CustomTabsProps {
  tabs: string[]
  activeIndex: number
  onTabChange?: (index: number) => void
  renderTab?: (
    label: string,
    index: number,
    ref: (el: HTMLElement | null) => void,
  ) => React.ReactNode
  used?: string
  badges?: boolean[]
}

const CustomTabs: React.FC<CustomTabsProps> = ({
  tabs,
  activeIndex,
  onTabChange,
  renderTab,
  used,
  badges,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLElement | null)[]>([])
  const [sliderStyle, setSliderStyle] = useState<{ left: string; width: string }>({
    left: '0px',
    width: '0px',
  })
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  const updateSliderPosition = useCallback(() => {
    if (tabRefs.current[activeIndex] && containerRef.current) {
      const activeTabElement = tabRefs.current[activeIndex]
      const containerElement = containerRef.current

      if (activeTabElement) {
        const containerRect = containerElement.getBoundingClientRect()
        const activeTabRect = activeTabElement.getBoundingClientRect()

        const scrollLeft = containerElement.scrollLeft
        const left = activeTabRect.left - containerRect.left + scrollLeft
        const width = activeTabRect.width

        setSliderStyle({
          left: `${left}px`,
          width: `${width}px`,
        })

        if (isFirstLoad) {
          setIsFirstLoad(false)
        }

        activeTabElement.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        })
      }
    }
  }, [activeIndex, isFirstLoad])

  useEffect(() => {
    updateSliderPosition()
  }, [updateSliderPosition])

  useEffect(() => {
    window.addEventListener('resize', updateSliderPosition)
    return () => {
      window.removeEventListener('resize', updateSliderPosition)
    }
  }, [updateSliderPosition])

  return (
    <div className={clsx(styles.tabsContainer, used && styles[used])} ref={containerRef}>
      {tabs.map((tab, index) =>
        renderTab ? (
          renderTab(tab, index, (el) => {
            tabRefs.current[index] = el
          })
        ) : (
          <button
            key={index}
            type='button'
            ref={(el) => {
              tabRefs.current[index] = el
            }}
            className={clsx(styles.tabButton, { [styles.active]: index === activeIndex })}
            onClick={() => onTabChange?.(index)}
          >
            {tab}
            {badges?.[index] && <Badge used='notification' />}
          </button>
        ),
      )}
      <div
        className={clsx(styles.tabsSlider, { [styles.noTransition]: isFirstLoad })}
        style={sliderStyle}
      />
    </div>
  )
}

export default CustomTabs
